/**
 * ProtectedLayout — the authenticated app shell, styled to the validated POC
 * admin (poc/admin.html + poc/css/admin.css).
 *
 * Look: a DARK teal sage sidebar (gradient primary-dark -> primary) with the
 * D.O.S.E logo pinned top, the POC nav sections, an active-item highlight, and
 * Logout pinned to the bottom. A sticky glass topbar carries a user search that
 * submits to `/users?search=`, a theme toggle and a user chip (name / role +
 * logout icon) sourced from `useAuth`. Collapses to a drawer below `lg`.
 *
 * The POC's notifications bell is deliberately absent — see the comment where
 * it used to sit.
 *
 * All sidebar sections now link to real, backend-backed pages (admin modules
 * AdminCommerceModule / AdminCopyModule / AdminRetentionModule /
 * AdminAdvancedModule). No "Soon" placeholders remain. Advanced Config and
 * Settings stay SUPER_ADMIN-only (server-guarded too).
 *
 * Keeps the real foundation contracts (`useAuth`, `useTheme`) intact.
 */
import {
  useState,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { cn } from '../lib/cn';

/* ── Inline icons (decorative) — mirror the POC stroke icons ─────────────── */

type IconProps = { className?: string };
const ico = 'h-[18px] w-[18px] shrink-0';

/** Per-viewer sidebar preference. */
const SIDEBAR_COLLAPSED_KEY = 'yd_admin_sidebar_collapsed';

function OverviewIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  );
}
function ContentIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <polygon points="10 8 16 11 10 14 10 8" fill="currentColor" />
      <line x1="8" y1="21" x2="16" y2="21" />
    </svg>
  );
}
function MediaIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}
function UsersIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function AffirmationsIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function MarketingIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M3 11l18-8v18l-18-8v-2z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}
/** Sidebar-collapse toggle: a panel with its rail, mirrored when collapsed. */
function SidebarToggleIcon({ className, collapsed }: IconProps & { collapsed: boolean }) {
  return (
    <svg
      className={cn('h-[18px] w-[18px] shrink-0', className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d={collapsed ? 'M14 9l3 3-3 3' : 'M17 9l-3 3 3 3'} />
    </svg>
  );
}

function AuditIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h4" />
    </svg>
  );
}

function ReferralsIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}
function FinanceIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
      <line x1="6" y1="15" x2="10" y2="15" />
    </svg>
  );
}
function AnnouncementsIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M3 11l18-8v18l-18-8v-2z" />
      <path d="M11 15v4a2 2 0 0 0 4 0v-1" />
    </svg>
  );
}
function EmailIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}
function SubscriptionsIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M2 9h20" />
      <path d="M6 14h4" />
    </svg>
  );
}
function NotificationIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
function CopyIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 7V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <rect x="4" y="9" width="12" height="12" rx="2" />
    </svg>
  );
}
/** A speech bubble with a tick: cue text, signed off. */
function CueReviewIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.9 9.9 0 0 1-4-.8L3 21l1.9-4.6A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5Z" />
      <polyline points="9 11.5 11 13.5 15.5 9" />
    </svg>
  );
}

function AppContentIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}
function CoachIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M8 9h8M8 13h5" />
    </svg>
  );
}
function PlanIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}
function ProgramsIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}
function ChallengesIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}
function GamificationIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="8" r="6" />
      <path d="M8.21 13.89 7 22l5-3 5 3-1.21-8.12" />
    </svg>
  );
}
function AdvancedIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  );
}
function VoiceIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="21" />
      <line x1="9" y1="21" x2="15" y2="21" />
    </svg>
  );
}
function SettingsIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function LogoutIcon({ className }: IconProps) {
  return (
    <svg className={cn(ico, className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
function SearchIcon({ className }: IconProps) {
  return (
    <svg className={cn('h-4 w-4 shrink-0', className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function MenuIcon({ className }: IconProps) {
  return (
    <svg className={cn('h-[22px] w-[22px]', className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}
function CloseIcon({ className }: IconProps) {
  return (
    <svg className={cn('h-5 w-5', className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function SunIcon({ className }: IconProps) {
  return (
    <svg className={cn('h-5 w-5', className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}
function MoonIcon({ className }: IconProps) {
  return (
    <svg className={cn('h-5 w-5', className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/* ── Nav model ───────────────────────────────────────────────────────────── */

interface NavItem {
  to: string;
  label: string;
  Icon: (p: IconProps) => ReactNode;
  superOnly?: boolean;
}

interface NavSection {
  heading?: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Overview', Icon: OverviewIcon },
      { to: '/content', label: 'Content', Icon: ContentIcon },
      // Next to Content rather than buried under Config: the 274 drafted cues
      // are invisible to every member until they are approved here, and the
      // reason none had been was that the only way in was to open 36 practices
      // one at a time. A worklist nobody can find is the same problem again.
      { to: '/cue-review', label: 'Cue Review', Icon: CueReviewIcon },
      { to: '/media', label: 'Media', Icon: MediaIcon },
      { to: '/users', label: 'Users', Icon: UsersIcon },
    ],
  },
  {
    heading: 'Comms & Commerce',
    items: [
      { to: '/marketing', label: 'Marketing', Icon: MarketingIcon },
      { to: '/referrals', label: 'Referrals', Icon: ReferralsIcon },
      { to: '/finance', label: 'Finance', Icon: FinanceIcon },
      { to: '/subscriptions', label: 'Subscriptions', Icon: SubscriptionsIcon },
      { to: '/announcements', label: 'Announcements', Icon: AnnouncementsIcon },
      { to: '/email-templates', label: 'Email Templates', Icon: EmailIcon },
    ],
  },
  {
    heading: 'Config & Copy',
    items: [
      { to: '/affirmations', label: 'Affirmations', Icon: AffirmationsIcon },
      { to: '/notification-templates', label: 'Notification Templates', Icon: NotificationIcon },
      { to: '/app-copy', label: 'App Copy', Icon: CopyIcon },
      { to: '/app-content', label: 'App Content', Icon: AppContentIcon },
      { to: '/coach-rules', label: 'Coach Rules', Icon: CoachIcon },
      { to: '/plan-config', label: 'Plan Config', Icon: PlanIcon },
    ],
  },
  {
    heading: 'Retention & Game',
    items: [
      { to: '/programs', label: 'Programs', Icon: ProgramsIcon },
      { to: '/gamification', label: 'Gamification', Icon: GamificationIcon },
      { to: '/challenges', label: 'Challenges', Icon: ChallengesIcon, superOnly: true },
    ],
  },
  {
    heading: 'System',
    items: [
      { to: '/advanced', label: 'Advanced Config', Icon: AdvancedIcon, superOnly: true },
      { to: '/audit', label: 'Audit Log', Icon: AuditIcon, superOnly: true },
      { to: '/settings', label: 'Settings', Icon: SettingsIcon, superOnly: true },
      { to: '/ai', label: 'AI Voice', Icon: VoiceIcon, superOnly: true },
    ],
  },
];

function roleLabel(role?: string | null): string {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Admin';
    case 'CONTENT_ADMIN':
      return 'Content Admin';
    default:
      return 'Administrator';
  }
}

/* ── Brand lockup (POC .admin-sidebar__logo) ─────────────────────────────── */

function Brand({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div className={cn('flex items-center gap-3', collapsed && 'justify-center')}>
      <span className="grid h-8 w-8 shrink-0 grid-cols-2 gap-0.5 rounded-lg bg-white/10 p-1.5">
        <span className="rounded-full bg-dopamine" />
        <span className="rounded-full bg-oxytocin" />
        <span className="rounded-full bg-serotonin" />
        <span className="rounded-full bg-endorphins" />
      </span>
      {!collapsed && (
        <span className="text-lg font-bold tracking-[0.06em] text-white">
          D<span className="text-dopamine">.</span>O
          <span className="text-dopamine">.</span>S
          <span className="text-dopamine">.</span>E
        </span>
      )}
    </div>
  );
}

function NavItems({
  isSuperAdmin,
  onNavigate,
  collapsed = false,
}: {
  isSuperAdmin: boolean;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  // Collapsed, the icon is all there is, so hovering has to name the
  // destination. Not the native `title` — it waits about a second and appears
  // at the cursor, not beside the row.
  //
  // One shared bubble, `position: fixed`, placed from the hovered link's own
  // rect. It cannot live inside the <nav>: that scrolls vertically, and CSS
  // forces the cross axis to clip too, so anything drawn to the right of an
  // icon would be cut off at the rail's edge.
  const [tip, setTip] = useState<{ label: string; top: number; left: number } | null>(null);

  const showTip =
    (label: string) => (e: ReactMouseEvent<HTMLElement> | ReactFocusEvent<HTMLElement>) => {
      if (!collapsed) return;
      const r = e.currentTarget.getBoundingClientRect();
      setTip({ label, top: r.top + r.height / 2, left: r.right + 10 });
    };
  const hideTip = () => setTip(null);

  return (
    <>
      <nav
        className={cn(
          'sidebar-scroll flex flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden py-4',
          collapsed ? 'px-2' : 'px-3',
        )}
        aria-label="Primary"
        // A wheel over the rail moves the icons out from under the bubble.
        onScroll={hideTip}
      >
        {NAV_SECTIONS.map((section, idx) => {
          const items = section.items.filter((i) => !i.superOnly || isSuperAdmin);
          if (items.length === 0) return null;
          return (
            <div key={section.heading ?? `nav-${idx}`} className={cn(idx > 0 && 'mt-3')}>
              {section.heading &&
                (collapsed ? (
                  // The section still needs to read as a break, but its heading
                  // will not fit in 72px. A rule keeps the grouping visible.
                  <div className="mx-2 mb-1 mt-2 border-t border-sidebar-border" />
                ) : (
                  <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/45">
                    {section.heading}
                  </p>
                ))}
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={onNavigate}
                  onMouseEnter={showTip(item.label)}
                  onMouseLeave={hideTip}
                  onFocus={showTip(item.label)}
                  onBlur={hideTip}
                  // Screen readers get the name from aria-label; the bubble is
                  // decorative and marked so, or it would be read twice.
                  aria-label={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn('nav-link', isActive && 'is-active', collapsed && 'justify-center px-0')
                  }
                >
                  <item.Icon className="opacity-90" />
                  {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {collapsed && tip && (
        <div
          role="presentation"
          aria-hidden="true"
          style={{ top: tip.top, left: tip.left }}
          className="pointer-events-none fixed z-50 -translate-y-1/2 whitespace-nowrap rounded-md bg-surface-dark px-2.5 py-1.5 text-xs font-medium text-white shadow-lg ring-1 ring-white/10"
        >
          {tip.label}
        </div>
      )}
    </>
  );
}

function SidebarBody({
  isSuperAdmin,
  onLogout,
  onNavigate,
  onClose,
  collapsed = false,
}: {
  isSuperAdmin: boolean;
  onLogout: () => void;
  onNavigate?: () => void;
  onClose?: () => void;
  collapsed?: boolean;
}) {
  return (
    <div className="flex h-full flex-col overflow-hidden bg-sidebar bg-cover text-white">
      {/* Header — logo top. Same 68px as the topbar so the two rules line up. */}
      <div
        className={cn(
          'flex h-[68px] shrink-0 items-center justify-between border-b border-sidebar-border',
          collapsed ? 'px-3' : 'px-5',
        )}
      >
        <Brand collapsed={collapsed} />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="rounded-lg p-1.5 text-white/80 hover:bg-sidebar-hover lg:hidden"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {/* Nav */}
      <NavItems isSuperAdmin={isSuperAdmin} onNavigate={onNavigate} collapsed={collapsed} />

      {/* Logout pinned to the bottom */}
      <div className={cn('border-t border-sidebar-border', collapsed ? 'p-2' : 'p-3')}>
        <button
          type="button"
          onClick={onLogout}
          title={collapsed ? 'Logout' : undefined}
          aria-label={collapsed ? 'Logout' : undefined}
          className={cn(
            'nav-link nav-link--logout w-full',
            collapsed && 'justify-center px-0',
          )}
        >
          <LogoutIcon className="opacity-90" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}

export function Layout() {
  const { isAuthenticated, user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  // Per-viewer convenience, so the rail stays how this operator left it.
  // localStorage can throw (private windows, blocked site data), and a sidebar
  // preference is never worth failing a render over.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleSidebar = () =>
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* not worth a broken toggle */
      }
      return next;
    });

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const initials = (user?.name ?? user?.email ?? '?')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-full min-h-screen bg-surface-muted dark:bg-surface-dark">
      {/* Sidebar — desktop (fixed dark teal rail) */}
      <aside
        className={cn(
          'hidden shrink-0 shadow-sidebar transition-[width] duration-200 lg:block',
          collapsed ? 'w-[72px]' : 'w-64',
        )}
      >
        <SidebarBody
          isSuperAdmin={Boolean(user?.role === 'SUPER_ADMIN')}
          onLogout={handleLogout}
          collapsed={collapsed}
        />
      </aside>

      {/* Sidebar — mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute left-0 top-0 h-full w-64 shadow-sidebar">
            <SidebarBody
              isSuperAdmin={Boolean(user?.role === 'SUPER_ADMIN')}
              onLogout={handleLogout}
              onNavigate={() => setMobileOpen(false)}
              onClose={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar — sticky glass (POC .admin-topbar) */}
        <header className="sticky top-0 z-30 flex h-[68px] items-center justify-between gap-3 border-b border-surface-border bg-surface/90 px-4 backdrop-blur-md dark:border-surface-dark-border dark:bg-surface-dark-muted/90 sm:px-6">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="rounded-lg p-1.5 text-surface-dark hover:bg-surface-subtle dark:text-surface dark:hover:bg-surface-dark-subtle lg:hidden"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </button>

            {/* Collapse the desktop rail to icons. Desktop only — below lg the
                sidebar is already a drawer, so there is nothing to collapse. */}
            <button
              type="button"
              className="hidden rounded-lg p-2 text-surface-dark/70 hover:bg-surface-subtle hover:text-surface-dark dark:text-surface/70 dark:hover:bg-surface-dark-subtle dark:hover:text-surface lg:inline-flex"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-pressed={collapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={toggleSidebar}
            >
              <SidebarToggleIcon collapsed={collapsed} />
            </button>

            {/*
              Search (POC pill). This was markup only — no value, no onChange,
              no submit handler — so an operator could type a name, press
              Enter, watch nothing happen, and reasonably conclude the user
              wasn't in the system. It now submits to `/users?search=`, which
              `GET /admin/users` has supported server-side all along.

              It says "Search users" rather than "Search" because that is what
              it does. `GET /admin/content` takes the same parameter and Content
              could be a second destination, but a box that silently picks one
              of two entity types is the same class of lie in a smaller size.
            */}
            <form
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                const q = search.trim();
                if (!q) return;
                navigate(`/users?search=${encodeURIComponent(q)}`);
              }}
              className="hidden items-center gap-2 rounded-full bg-surface-subtle px-3 py-2 text-surface-dark/50 focus-within:ring-2 focus-within:ring-primary/40 dark:bg-surface-dark-subtle dark:text-surface/50 sm:flex sm:w-64 lg:w-72"
            >
              <SearchIcon />
              <input
                type="search"
                placeholder="Search users…"
                aria-label="Search users"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent text-sm text-surface-dark outline-none placeholder:text-surface-dark/45 dark:text-surface dark:placeholder:text-surface/45"
              />
            </form>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Theme toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="grid h-[38px] w-[38px] place-items-center rounded-full bg-surface-subtle text-surface-dark/60 transition-colors hover:bg-primary/10 hover:text-primary dark:bg-surface-dark-subtle dark:text-surface/60"
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>

            {/*
              The notifications bell was REMOVED, not disabled. It had no
              onClick and its badge was the literal character `3` — so it told
              every operator, on every page, forever, that three things needed
              their attention, and then did nothing when clicked. A permanent
              false alert is worse than no alert: it trains the person to
              ignore the one place we would put a real one.

              There is nothing behind it to show. The admin API exposes no
              notification, alert or unread concept (only `GET /admin/audit`,
              which already has its own page). Restoring the bell is a feature
              — a source first, then a surface — not a re-render of this markup.
            */}
            {/* User chip (name / role + logout icon) */}
            <div className="flex items-center gap-2.5 rounded-full bg-surface-subtle py-1 pl-1 pr-1.5 dark:bg-surface-dark-subtle">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-primary to-primary-400 text-xs font-bold text-white">
                {initials}
              </span>
              <div className="hidden leading-tight sm:block">
                <p className="text-sm font-semibold text-surface-dark dark:text-surface">
                  {user?.name ?? user?.email ?? 'Admin'}
                </p>
                <p className="text-[11px] text-surface-dark/45 dark:text-surface/45">
                  {roleLabel(user?.role)}
                </p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                aria-label="Sign out"
                title="Sign out"
                className="grid h-[38px] w-[38px] place-items-center rounded-full text-surface-dark/55 transition-colors hover:bg-surface-border hover:text-surface-dark dark:text-surface/55 dark:hover:bg-surface-dark-border dark:hover:text-surface"
              >
                <LogoutIcon />
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="mx-auto w-full max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
